import axios from 'axios'
import setAuthToken from '../../../utils/setAuthToken'
import jwt_decode from 'jwt-decode'

import {
  SET_CURRENT_USER,
  GET_ERRORS,
  CHANGE_DIVISION,
  UPDATE_USER_INFO,
  PASSWORD_RESETTING,
  TOKEN_REFRESHED,
  SET_RC_SESSION,
} from './types'
import { getImage } from '../../Users/redux/userActions'
import { clearErrors } from '../../../actions/errorsActions'
import { notificationDanger, notificationInfo, notificationSuccess } from '../../../utils/customToastify'

export const getRefreshToken = () => async (dispatch) => {
  try {
    const response = await axios.post('/auth/refresh-token')
    const token = response?.data?.token
    if (!token) throw new Error('No token in refresh response')
    // Persist AND set headers (previously only the axios header was updated, leaving a stale
    // CRMToken in sessionStorage that sockets and the 401 interceptor still read).
    sessionStorage.setItem('CRMToken', token)
    setAuthToken(token)
    const decoded = jwt_decode(token)
    dispatch(setCurrentUser(decoded))
    dispatch(getImage(decoded.userId, decoded.imageId, true))
    dispatch(tokenRefreshed(true))
    // Safety net: a freshly minted token should never already be expired.
    const currentTime = Date.now() / 1000
    if (decoded.exp < currentTime) {
      dispatch(logoutUser())
    }
  } catch (error) {
    // A failed refresh is only fatal when the access token we already hold is itself unusable.
    // It is NOT fatal when there is simply nothing to refresh with — a tab logged in before
    // sessions existed, or one whose session row was ended elsewhere, still has a perfectly
    // valid JWT. Logging out there tore down a working login on every fresh mount, which is
    // every `window.open` row click and every reload.
    const currentToken = sessionStorage.getItem('CRMToken')
    if (!currentToken) return

    let tokenUnusable = true
    try {
      tokenUnusable = jwt_decode(currentToken).exp < Date.now() / 1000
    } catch (decodeError) {
      tokenUnusable = true
    }

    if (tokenUnusable) {
      dispatch(logoutUser())
      return
    }

    // Keep the still-valid token and let auth-gated screens continue. SignInAs blocks on this
    // flag, so leaving it false would hang the login-as popup on a spinner forever.
    dispatch(tokenRefreshed(true))
  }
}

const tokenRefreshed = (isRefreshed) => (dispatch) => {
  dispatch({ type: TOKEN_REFRESHED, payload: isRefreshed })
}

export const loginUser = (userData, type) => async (dispatch) => {
  try {
    let path = type ? '/auth/login-as-user' : '/auth/session'
    const res = await axios.post(path, userData)
    const { token, sessionToken } = res.data

    // Save tokens to sessionStorage first
    sessionStorage.setItem('CRMToken', token)
    if (sessionToken) {
      sessionStorage.setItem('sessionToken', sessionToken)
    }

    // Set auth headers (this will also set sessionToken header from sessionStorage)
    setAuthToken(token)

    const decoded = jwt_decode(token)
    dispatch(setCurrentUser(decoded))
    dispatch(getImage(decoded.userId, decoded.imageId, true))
    dispatch(clearErrors())

    return true
  } catch (error) {
    return false
  }
}

export const updateUserInfo = (data) => async (dispatch) => {
  try {
    dispatch({
      type: UPDATE_USER_INFO,
      payload: data,
    })
  } catch (error) {
    throw new Error(error)
  }
}

export const changeDivision = (data) => (dispatch) => {
  dispatch({
    type: CHANGE_DIVISION,
    payload: data,
  })
}

// Set loged in user
export const setCurrentUser = (decoded) => {
  return {
    type: SET_CURRENT_USER,
    payload: decoded,
  }
}

export const setRcSessionData = (data) => {
  return {
    type: SET_RC_SESSION,
    payload: data,
  }
}

// Logout user
export const logoutUser = (fromCps) => async (dispatch) => {
  try {
    const sessionToken = sessionStorage.getItem('sessionToken')

    // Call logout endpoint to end session in database
    if (sessionToken) {
      await axios.post(
        '/auth/logout',
        {},
        {
          headers: {
            'x-session-token': sessionToken,
          },
        }
      )
    }
  } finally {
    // Clear session storage and redirect
    sessionStorage.clear()
    setAuthToken(false)
    dispatch(setCurrentUser({}))
    if (!fromCps) {
      window.location.href = '/login'
    }
  }
}

// change user's password
export const changeTeamUserPassword = ({ userId, data, callbacks = { success: () => {} } }) => (dispatch) => {
  const { password, confirmPassword } = data

  if (password && password === confirmPassword) {
    return axios
      .post(`/auth/change-team-user-password/${userId}`, data)
      .then(() => {
        notificationSuccess('Successfully changed password.')
        dispatch(clearErrors())
        callbacks.success()
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          notificationDanger(err.response?.data?.message || 'Password change is not allowed for this user.')
        }
        dispatch({
          type: GET_ERRORS,
          payload: err.response?.data || {},
        })
      })
  } else {
    notificationInfo("password and confirm password don't match")
  }
}

// change your password
export const changePassword = (userData, userId) => (dispatch) => {
  const { newPassword, confirmPassword } = userData
  if (newPassword && confirmPassword && newPassword === confirmPassword) {
    return axios
      .post(`/auth/change-pass/${userId}`, userData)
      .then(() => {
        notificationSuccess('Successfully changed password.')
        dispatch(clearErrors())
      })
      .catch((err) => {
        const message = err.response?.data?.message || 'Unable to change password.'
        if (err.response?.status === 403) {
          notificationDanger(message)
        }
        dispatch({
          type: GET_ERRORS,
          payload: err.response?.data || {},
        })
        throw new Error()
      })
  } else {
    notificationInfo('Please confirm new Password')
    throw new Error()
  }
}

export const forgotPassword = (email) => async (dispatch) => {
  try {
    // eslint-disable-next-line
    const pattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

    if (pattern.test(email)) {
      const res = await axios.post(`/auth/forgot-password`, { email })
      console.log(res.body)
      dispatch(clearErrors())
      notificationSuccess('Instructions how to change password have been sent to your email.')
    } else {
      notificationDanger('Invalid Email!')
    }
  } catch (error) {
    //
  }
}

/** reset password via token sent to user's email */
export const resetPassword = (token, password) => async (dispatch) => {
  try {
    const res = await axios.post(`/auth/reset-password-by-token`, { token, password })
    dispatch({ type: PASSWORD_RESETTING, payload: res.data?.success })
    if (res.data?.success) {
      notificationSuccess('Password has been reset!')
    } else {
      notificationDanger('Password reset failed!')
    }
  } catch (error) {
    notificationDanger('Password reset failed!')
  }
}
